// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Roulette
// American roulette with premium visual wheel and betting grid
// ================================================================
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ProvablyFairEngine from '../../engine/ProvablyFairEngine';
import { useCasino } from '../../context/CasinoShell';

const RED_NUMBERS = [1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36];
const NUMBERS = [0, 28, 9, 26, 30, 11, 7, 20, 32, 17, 5, 22, 34, 15, 3, 24, 36, 13, 1, '00', 27, 10, 25, 29, 12, 8, 19, 31, 18, 6, 21, 33, 16, 4, 23, 35, 14, 2];

function getColor(n) {
  if (n === 0 || n === '00') return '#16A34A';
  return RED_NUMBERS.includes(typeof n === 'string' ? parseInt(n) : n) ? '#DC2626' : '#1E293B';
}

const CHIP_VALUES = [1, 5, 10, 25, 100];

// Bet types and payouts
const BET_TYPES = {
  straight: { payout: 35, label: 'Straight Up' },
  red: { payout: 1, label: 'Red' },
  black: { payout: 1, label: 'Black' },
  odd: { payout: 1, label: 'Odd' },
  even: { payout: 1, label: 'Even' },
  low: { payout: 1, label: '1-18' },
  high: { payout: 1, label: '19-36' },
  dozen1: { payout: 2, label: '1st 12' },
  dozen2: { payout: 2, label: '2nd 12' },
  dozen3: { payout: 2, label: '3rd 12' },
  col1: { payout: 2, label: '2:1 Col' },
  col2: { payout: 2, label: '2:1 Col' },
  col3: { payout: 2, label: '2:1 Col' },
};

export default function Roulette() {
  const { forgeBalance, placeBet, recordWin, recordLoss, logFairness, isExcluded } = useCasino();
  const engineRef = useRef(new ProvablyFairEngine());

  const [selectedChip, setSelectedChip] = useState(5);
  const [bets, setBets] = useState([]); // { type, number, amount }
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [wheelAngle, setWheelAngle] = useState(0);
  const [lastNumbers, setLastNumbers] = useState([]);
  const [commitmentHash, setCommitmentHash] = useState(null);
  const [lastWin, setLastWin] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    async function init() {
      const commitment = await engineRef.current.createCommitment();
      setCommitmentHash(commitment.commitmentHash);
    }
    init();
  }, []);

  const totalBetAmount = bets.reduce((s, b) => s + b.amount, 0);

  const addBet = useCallback((type, number = null) => {
    if (spinning) return;
    if (forgeBalance < selectedChip + totalBetAmount) return;
    setBets(prev => {
      const existing = prev.find(b => b.type === type && b.number === number);
      if (existing) {
        return prev.map(b => b.type === type && b.number === number
          ? { ...b, amount: b.amount + selectedChip } : b);
      }
      return [...prev, { type, number, amount: selectedChip }];
    });
  }, [spinning, forgeBalance, selectedChip, totalBetAmount]);

  const clearBets = () => { if (!spinning) setBets([]); };

  const checkBetWin = (bet, winNum) => {
    const n = typeof winNum === 'string' ? winNum : winNum;
    const numInt = typeof n === 'string' ? -1 : n;

    switch (bet.type) {
      case 'straight': return bet.number === n || bet.number === numInt;
      case 'red': return numInt > 0 && RED_NUMBERS.includes(numInt);
      case 'black': return numInt > 0 && !RED_NUMBERS.includes(numInt);
      case 'odd': return numInt > 0 && numInt % 2 === 1;
      case 'even': return numInt > 0 && numInt % 2 === 0;
      case 'low': return numInt >= 1 && numInt <= 18;
      case 'high': return numInt >= 19 && numInt <= 36;
      case 'dozen1': return numInt >= 1 && numInt <= 12;
      case 'dozen2': return numInt >= 13 && numInt <= 24;
      case 'dozen3': return numInt >= 25 && numInt <= 36;
      case 'col1': return numInt > 0 && numInt % 3 === 1;
      case 'col2': return numInt > 0 && numInt % 3 === 2;
      case 'col3': return numInt > 0 && numInt % 3 === 0;
      default: return false;
    }
  };

  const spinWheel = useCallback(async () => {
    if (spinning || bets.length === 0) return;
    if (isExcluded()) return;

    const betResult = placeBet(totalBetAmount, 'roulette');
    if (!betResult.success) return;

    setSpinning(true);
    setResult(null);
    setLastWin(null);

    const clientSeed = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    const fairResult = await engineRef.current.generateResult(clientSeed);
    const proof = engineRef.current.getProof();

    // Map result to roulette number (0-37, where 37 = 00)
    const idx = Math.floor(fairResult.resultValue * 38);
    const allNumbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, '00'];
    const winningNumber = allNumbers[idx];

    // Animate wheel (calculate target angle)
    const winIdx = NUMBERS.indexOf(winningNumber);
    const sliceAngle = 360 / NUMBERS.length;
    const targetAngle = wheelAngle + 1440 + (winIdx * sliceAngle); // 4 full rotations + position

    // Smooth animation
    const startAngle = wheelAngle;
    const duration = 4000;
    const startTime = Date.now();

    const animateWheel = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setWheelAngle(startAngle + (targetAngle - startAngle) * eased);
      if (progress < 1) requestAnimationFrame(animateWheel);
    };
    requestAnimationFrame(animateWheel);

    // Wait for animation
    await new Promise(r => setTimeout(r, duration + 500));

    // Calculate winnings
    let totalWinnings = 0;
    for (const bet of bets) {
      if (checkBetWin(bet, winningNumber)) {
        const payout = BET_TYPES[bet.type].payout;
        totalWinnings += bet.amount + (bet.amount * payout);
      }
    }

    if (totalWinnings > 0) {
      recordWin(totalWinnings, 'roulette', proof.resultHash);
      setLastWin(totalWinnings - totalBetAmount);
    } else {
      recordLoss(totalBetAmount, 'roulette', proof.resultHash);
    }

    setResult({ number: winningNumber, color: getColor(winningNumber), totalWinnings });
    setLastNumbers(prev => [winningNumber, ...prev].slice(0, 20));
    setHistory(prev => [{
      number: winningNumber,
      color: getColor(winningNumber),
      bet: totalBetAmount,
      win: totalWinnings,
      time: new Date().toLocaleTimeString(),
    }, ...prev].slice(0, 20));

    logFairness({ gameId: 'roulette', ...proof, betAmount: totalBetAmount, winAmount: totalWinnings });

    setSpinning(false);
    setBets([]);

    const nextCommitment = await engineRef.current.createCommitment();
    setCommitmentHash(nextCommitment.commitmentHash);
  }, [spinning, bets, totalBetAmount, placeBet, isExcluded, recordWin, recordLoss, logFairness, wheelAngle]);

  // Grid layout: 3 columns x 12 rows
  const gridNumbers = [];
  for (let row = 0; row < 12; row++) {
    for (let col = 2; col >= 0; col--) {
      gridNumbers.push(row * 3 + col + 1);
    }
  }

  return (
    <div style={s.container}>
      <style>{`
        @keyframes ballBounce { 0%,100%{transform:scale(1)} 50%{transform:scale(1.2)} }
        @keyframes winGlow { 0%,100%{box-shadow:0 0 20px #FFD70066} 50%{box-shadow:0 0 40px #FFD700AA} }
      `}</style>

      {/* Header */}
      <div style={s.header}>
        <h2 style={s.title}>ROULETTE</h2>
        <div style={s.headerRight}>
          <span style={s.balVal}>${forgeBalance.toFixed(2)}</span>
          {lastWin !== null && lastWin > 0 && (
            <span style={s.winBadge}>+${lastWin.toFixed(2)}</span>
          )}
        </div>
      </div>

      {/* Wheel visualization */}
      <div style={s.wheelSection}>
        <div style={s.wheelOuter}>
          <div style={{
            ...s.wheel,
            transform: `rotate(${wheelAngle}deg)`,
            transition: spinning ? 'none' : 'transform 0.3s ease',
          }}>
            {NUMBERS.map((n, i) => {
              const angle = (i / NUMBERS.length) * 360;
              return (
                <div key={i} style={{
                  position: 'absolute',
                  width: '100%', height: '100%',
                  transform: `rotate(${angle}deg)`,
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 2, left: '50%', transform: 'translateX(-50%)',
                    fontSize: 7, fontWeight: 800,
                    color: '#fff',
                    textShadow: '0 0 2px rgba(0,0,0,0.8)',
                  }}>
                    {n}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Ball indicator */}
          <div style={s.ballIndicator}>{'▼'}</div>
          {/* Center */}
          <div style={s.wheelCenter}>
            {result ? (
              <div style={{ textAlign: 'center' }}>
                <span style={{ ...s.resultNum, color: result.color === '#DC2626' ? '#EF4444' : result.color === '#16A34A' ? '#22C55E' : '#F8FAFC' }}>
                  {result.number === '00' ? '00' : result.number}
                </span>
                {result.totalWinnings > 0 && (
                  <span style={s.resultWin}>+${result.totalWinnings.toFixed(2)}</span>
                )}
              </div>
            ) : (
              <span style={s.wheelLogo}>{'🎯'}</span>
            )}
          </div>
        </div>

        {/* Last numbers */}
        {lastNumbers.length > 0 && (
          <div style={s.lastNumbers}>
            {lastNumbers.slice(0, 10).map((n, i) => (
              <span key={i} style={{
                ...s.lastNum,
                background: getColor(n),
                opacity: 1 - (i * 0.08),
              }}>{n}</span>
            ))}
          </div>
        )}
      </div>

      {/* Betting Grid */}
      <div style={s.gridSection}>
        {/* 0 and 00 */}
        <div style={s.zeroRow}>
          <button style={{ ...s.zeroBet, background: bets.some(b => b.number === 0) ? '#16A34A' : '#0a2010' }}
            onClick={() => addBet('straight', 0)}>0</button>
          <button style={{ ...s.zeroBet, background: bets.some(b => b.number === '00') ? '#16A34A' : '#0a2010' }}
            onClick={() => addBet('straight', '00')}>00</button>
        </div>

        {/* Number grid */}
        <div style={s.numGrid}>
          {[...Array(12)].map((_, row) => (
            <div key={row} style={s.gridRow}>
              {[3, 2, 1].map(col => {
                const num = row * 3 + col;
                const isRed = RED_NUMBERS.includes(num);
                const hasBet = bets.some(b => b.type === 'straight' && b.number === num);
                return (
                  <button key={num} style={{
                    ...s.numBtn,
                    background: hasBet
                      ? (isRed ? '#DC2626' : '#334155')
                      : (isRed ? '#7F1D1D' : '#0F172A'),
                    borderColor: hasBet ? '#FFD700' : (isRed ? '#DC262644' : '#334155'),
                    boxShadow: hasBet ? '0 0 8px #FFD70044' : 'none',
                  }} onClick={() => addBet('straight', num)}>
                    {num}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Column bets */}
        <div style={s.colRow}>
          {['col1', 'col2', 'col3'].map(col => (
            <button key={col} style={s.colBtn} onClick={() => addBet(col)}>2:1</button>
          ))}
        </div>

        {/* Dozen bets */}
        <div style={s.dozenRow}>
          <button style={s.dozenBtn} onClick={() => addBet('dozen1')}>1st 12</button>
          <button style={s.dozenBtn} onClick={() => addBet('dozen2')}>2nd 12</button>
          <button style={s.dozenBtn} onClick={() => addBet('dozen3')}>3rd 12</button>
        </div>

        {/* Outside bets */}
        <div style={s.outsideRow}>
          <button style={s.outsideBtn} onClick={() => addBet('low')}>1-18</button>
          <button style={s.outsideBtn} onClick={() => addBet('even')}>EVEN</button>
          <button style={{ ...s.outsideBtn, background: '#7F1D1D', borderColor: '#DC262666' }} onClick={() => addBet('red')}>RED</button>
          <button style={{ ...s.outsideBtn, background: '#0F172A', borderColor: '#33415566' }} onClick={() => addBet('black')}>BLACK</button>
          <button style={s.outsideBtn} onClick={() => addBet('odd')}>ODD</button>
          <button style={s.outsideBtn} onClick={() => addBet('high')}>19-36</button>
        </div>
      </div>

      {/* Chip selector + actions */}
      <div style={s.chipBar}>
        <div style={s.chips}>
          {CHIP_VALUES.map(v => (
            <button key={v} style={{
              ...s.chip,
              background: selectedChip === v
                ? 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)'
                : 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
              borderColor: selectedChip === v ? '#EF4444' : '#334155',
              transform: selectedChip === v ? 'scale(1.15)' : 'scale(1)',
            }} onClick={() => setSelectedChip(v)}>
              ${v}
            </button>
          ))}
        </div>
        <div style={s.actionRow}>
          <button style={s.clearBtn} onClick={clearBets} disabled={spinning}>CLEAR</button>
          <button style={{
            ...s.spinBtn,
            opacity: bets.length === 0 || spinning ? 0.4 : 1,
          }} onClick={spinWheel} disabled={spinning || bets.length === 0}>
            {spinning ? 'SPINNING...' : `SPIN • $${totalBetAmount.toFixed(2)}`}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div style={s.footer}>
        <span>American Roulette • 0 & 00 • Provably Fair SHA-256</span>
      </div>
    </div>
  );
}

const s = {
  container: { fontFamily: "'SF Pro Display', -apple-system, sans-serif" },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px',
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
    borderBottom: '1px solid #334155',
  },
  title: { fontSize: 16, fontWeight: 800, color: '#F8FAFC', letterSpacing: 4, margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  balVal: { fontSize: 14, fontWeight: 800, color: '#FFD700', fontFamily: 'monospace' },
  winBadge: { fontSize: 11, fontWeight: 800, color: '#22C55E', padding: '2px 8px', background: '#22C55E22', borderRadius: 10 },
  wheelSection: {
    padding: '16px',
    background: 'linear-gradient(180deg, #14532D 0%, #166534 100%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    borderBottom: '3px solid #854D0E',
  },
  wheelOuter: {
    position: 'relative',
    width: 180, height: 180,
  },
  wheel: {
    width: '100%', height: '100%',
    borderRadius: '50%',
    background: 'conic-gradient(from 0deg, #DC2626 0deg 9.47deg, #1E293B 9.47deg 18.95deg, #DC2626 18.95deg 28.42deg, #1E293B 28.42deg 37.89deg, #16A34A 37.89deg 47.37deg, #DC2626 47.37deg 56.84deg, #1E293B 56.84deg 66.32deg, #DC2626 66.32deg 75.79deg, #1E293B 75.79deg 85.26deg, #DC2626 85.26deg 94.74deg, #1E293B 94.74deg 104.21deg, #DC2626 104.21deg 113.68deg, #1E293B 113.68deg 123.16deg, #DC2626 123.16deg 132.63deg, #1E293B 132.63deg 142.11deg, #DC2626 142.11deg 151.58deg, #1E293B 151.58deg 161.05deg, #DC2626 161.05deg 170.53deg, #16A34A 170.53deg 180deg, #1E293B 180deg 189.47deg, #DC2626 189.47deg 198.95deg, #1E293B 198.95deg 208.42deg, #DC2626 208.42deg 217.89deg, #1E293B 217.89deg 227.37deg, #DC2626 227.37deg 236.84deg, #1E293B 236.84deg 246.32deg, #DC2626 246.32deg 255.79deg, #1E293B 255.79deg 265.26deg, #DC2626 265.26deg 274.74deg, #1E293B 274.74deg 284.21deg, #DC2626 284.21deg 293.68deg, #1E293B 293.68deg 303.16deg, #DC2626 303.16deg 312.63deg, #1E293B 312.63deg 322.11deg, #DC2626 322.11deg 331.58deg, #1E293B 331.58deg 341.05deg, #DC2626 341.05deg 350.53deg, #1E293B 350.53deg 360deg)',
    border: '4px solid #854D0E',
    boxShadow: '0 0 20px rgba(0,0,0,0.5), inset 0 0 20px rgba(0,0,0,0.3)',
  },
  ballIndicator: {
    position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)',
    fontSize: 14, color: '#FFD700', zIndex: 5,
    textShadow: '0 0 8px #FFD700',
  },
  wheelCenter: {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 60, height: 60, borderRadius: '50%',
    background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
    border: '3px solid #854D0E',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2,
  },
  resultNum: { fontSize: 20, fontWeight: 900, display: 'block' },
  resultWin: { fontSize: 8, fontWeight: 700, color: '#FFD700', display: 'block' },
  wheelLogo: { fontSize: 24 },
  lastNumbers: {
    display: 'flex', gap: 4, marginTop: 12, flexWrap: 'wrap', justifyContent: 'center',
  },
  lastNum: {
    width: 22, height: 22, borderRadius: 4,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 9, fontWeight: 800, color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  gridSection: {
    padding: '8px',
    background: '#14532D',
  },
  zeroRow: {
    display: 'flex', gap: 2, marginBottom: 2,
  },
  zeroBet: {
    flex: 1, padding: '8px', borderRadius: 4,
    border: '1px solid #22C55E44',
    color: '#22C55E', fontSize: 12, fontWeight: 900,
    cursor: 'pointer', textAlign: 'center',
  },
  numGrid: {
    display: 'flex', flexDirection: 'column', gap: 1,
  },
  gridRow: {
    display: 'flex', gap: 1,
  },
  numBtn: {
    flex: 1, padding: '6px 0',
    borderRadius: 3, border: '1px solid',
    color: '#F8FAFC', fontSize: 11, fontWeight: 800,
    cursor: 'pointer', textAlign: 'center',
    transition: 'all 0.1s ease',
  },
  colRow: {
    display: 'flex', gap: 2, marginTop: 2,
  },
  colBtn: {
    flex: 1, padding: '6px',
    borderRadius: 3, border: '1px solid #334155',
    background: '#0F172A', color: '#94A3B8',
    fontSize: 9, fontWeight: 800, cursor: 'pointer', textAlign: 'center',
  },
  dozenRow: {
    display: 'flex', gap: 2, marginTop: 4,
  },
  dozenBtn: {
    flex: 1, padding: '8px',
    borderRadius: 4, border: '1px solid #334155',
    background: '#0F172A', color: '#CBD5E1',
    fontSize: 10, fontWeight: 800, cursor: 'pointer', textAlign: 'center',
  },
  outsideRow: {
    display: 'flex', gap: 2, marginTop: 4,
  },
  outsideBtn: {
    flex: 1, padding: '8px 2px',
    borderRadius: 4, border: '1px solid #334155',
    background: '#0F172A', color: '#CBD5E1',
    fontSize: 8, fontWeight: 800, cursor: 'pointer', textAlign: 'center',
  },
  chipBar: {
    padding: '12px 16px',
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
    borderTop: '3px solid #854D0E',
  },
  chips: {
    display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 10,
  },
  chip: {
    width: 40, height: 40, borderRadius: '50%',
    border: '2px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 9, fontWeight: 800, color: '#F8FAFC',
    cursor: 'pointer', transition: 'all 0.15s ease',
  },
  actionRow: {
    display: 'flex', gap: 8,
  },
  clearBtn: {
    padding: '10px 16px', borderRadius: 8,
    border: '1px solid #475569', background: '#1E293B',
    color: '#94A3B8', fontSize: 10, fontWeight: 800,
    letterSpacing: 1, cursor: 'pointer',
  },
  spinBtn: {
    flex: 1, padding: '12px',
    borderRadius: 10, border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #B91C1C 100%)',
    color: '#fff', fontSize: 13, fontWeight: 900,
    letterSpacing: 2, cursor: 'pointer',
    boxShadow: '0 4px 16px #DC262644',
  },
  footer: {
    padding: '10px 16px', textAlign: 'center',
    fontSize: 8, color: '#475569', letterSpacing: 1,
    background: '#0F172A',
  },
};
